import React, { useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { Box, Button, List, ListItemButton, Typography } from '@mui/material';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import ThumbsUpDownOutlinedIcon from '@mui/icons-material/ThumbsUpDownOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import {
  CRM_CONTACT_LIST_NAMES,
  CRM_CONTACT_OWNER_OPTIONS,
  CRM_CONTACT_SOURCE_OPTIONS,
  CRM_CONTACT_STAGE_OPTIONS,
  CRM_CONTACT_SUPPORT_OPTIONS,
} from '../contacts.queries';
import { ContactListState } from '../contactList';
import {
  ConsentEditor,
  ContactedEditor,
  CountryEditor,
  JoinedEditor,
  ListsEditor,
  OwnerEditor,
  SourceEditor,
  StageEditor,
  SupportEditor,
  TagsEditor,
} from './editors';
import {
  activeFilterCount,
  clearFilter,
  describeFilter,
  EXTRA_FILTERS,
  FILTER_LABELS,
  FilterId,
  FilterNames,
  hasBackwardsDates,
  isFilterActive,
  PINNED_FILTERS,
} from './filterModel';
import { FilterPill } from './FilterPill';
import { FilterSurface } from './FilterSurface';
import { ConsentCounts, FacetOption, FilterOptions, ListOption } from './types';

const ICONS: Record<FilterId, React.ReactNode> = {
  tags: <LocalOfferOutlinedIcon />,
  lists: <FormatListBulletedOutlinedIcon />,
  country: <PublicOutlinedIcon />,
  consent: <VerifiedUserOutlinedIcon />,
  joined: <EventAvailableOutlinedIcon />,
  contacted: <HistoryOutlinedIcon />,
  support: <ThumbsUpDownOutlinedIcon />,
  owner: <AssignmentIndOutlinedIcon />,
  source: <SourceOutlinedIcon />,
  stage: <FlagOutlinedIcon />,
};

/** Wider panels for the calendars and the consent rows. */
const WIDTHS: Partial<Record<FilterId | 'more', number>> = {
  joined: 560,
  contacted: 560,
  consent: 500,
  more: 320,
};

type OpenPanel =
  | { id: 'more' }
  /** `viaMore`: reached from "More filters", so anchored there with a way back. */
  | { id: FilterId; viaMore?: boolean };

interface FilterBarProps {
  state: ContactListState;
  options: FilterOptions | undefined;
  /** The current filter's consent breakdown, for the consent panel's numbers. */
  consent: ConsentCounts | undefined;
  /** "1,457 contacts", shown in each panel as its changes apply. */
  matchCount: string;
  onChange: (change: Partial<ContactListState>) => void;
  /** Clears the search too. */
  onClearAll: () => void;
  /** A search is set, so "Clear all" has something to clear even with no filter. */
  searching: boolean;
}

/**
 * The filters, one pill each: the common ones always on the bar, the rest
 * behind "More filters" until set. A set filter's pill says its value and has
 * a ×; a click opens its panel. Every change applies at once.
 */
export const FilterBar = ({ state, options, consent, matchCount, onChange, onClearAll, searching }: FilterBarProps) => {
  const [open, setOpen] = useState<OpenPanel | null>(null);
  // What the panel shows: the open one, or while it fades out, the one just closed.
  const lastOpen = useRef<OpenPanel | null>(null);
  if (open) lastOpen.current = open;
  const shownPanel = open ?? lastOpen.current;
  const pills = useRef<Partial<Record<FilterId | 'more', HTMLDivElement | null>>>({});
  const knownLists = useRef(new Map<number, ListOption>());
  const today = dayjs().format('YYYY-MM-DD');

  options?.lists.forEach((list) => knownLists.current.set(list.id, list));

  // Chosen lists outside the first 200, as from a pasted link: named by id.
  const unnamed = options ? state.listIds.filter((id) => !knownLists.current.has(id)) : [];
  const listNames = useQuery<{ crmSegments: { items: Array<ListOption> } }>(CRM_CONTACT_LIST_NAMES, {
    variables: { ids: unnamed },
    skip: unnamed.length === 0,
  });
  listNames.data?.crmSegments.items.forEach((list) => knownLists.current.set(list.id, list));

  // Asked once a panel needs them, and kept (from the cache) while it fades out.
  const shownId = shownPanel?.id;
  const owners = useQuery<{ owners: Array<FacetOption> }>(CRM_CONTACT_OWNER_OPTIONS, {
    skip: shownId !== 'owner' && state.ownerIds.length === 0,
  });
  const sources = useQuery<{ sources: Array<FacetOption> }>(CRM_CONTACT_SOURCE_OPTIONS, { skip: shownId !== 'source' });
  const stages = useQuery<{ stages: Array<FacetOption> }>(CRM_CONTACT_STAGE_OPTIONS, { skip: shownId !== 'stage' });
  const support = useQuery<{ supportLevels: Array<FacetOption> }>(CRM_CONTACT_SUPPORT_OPTIONS, { skip: shownId !== 'support' });

  const tagNames = new Map((options?.tags ?? []).map((tag) => [tag.id, tag.name]));
  const names: FilterNames = {
    tag: (id) => tagNames.get(id) ?? `Tag #${id}`,
    list: (id) => knownLists.current.get(id)?.name ?? `List #${id}`,
    owner: (id) => owners.data?.owners.find((owner) => owner.value === String(id))?.label ?? `User #${id}`,
  };

  // A set extra gets its own pill; so does one whose panel was opened from it, until it closes.
  const ownPanel = open && open.id !== 'more' && !('viaMore' in open && open.viaMore) ? open.id : undefined;
  const shown = [...PINNED_FILTERS, ...EXTRA_FILTERS.filter((id) => isFilterActive(id, state) || id === ownPanel)];
  const extrasSet = EXTRA_FILTERS.filter((id) => isFilterActive(id, state)).length;

  const close = () => setOpen(null);

  const editor = (id: FilterId) => {
    switch (id) {
      case 'tags':
        return <TagsEditor state={state} options={options} onChange={onChange} />;
      case 'lists':
        return <ListsEditor state={state} options={options} known={knownLists.current} onChange={onChange} />;
      case 'country':
        return <CountryEditor state={state} options={options} onChange={onChange} />;
      case 'consent':
        return <ConsentEditor state={state} counts={consent} onChange={onChange} />;
      case 'joined':
        return <JoinedEditor state={state} today={today} onChange={onChange} />;
      case 'contacted':
        return <ContactedEditor state={state} today={today} onChange={onChange} />;
      case 'support':
        return <SupportEditor state={state} levels={support.data?.supportLevels} onChange={onChange} />;
      case 'owner':
        return <OwnerEditor state={state} owners={owners.data?.owners} onChange={onChange} />;
      case 'source':
        return <SourceEditor state={state} sources={sources.data?.sources} onChange={onChange} />;
      case 'stage':
        return <StageEditor state={state} stages={stages.data?.stages} onChange={onChange} />;
      default:
        return null;
    }
  };

  const moreMenu = (
    <List disablePadding aria-label="More filters" sx={{ mx: -1 }}>
      {EXTRA_FILTERS.map((id) => {
        const summary = describeFilter(id, state, names, today);
        return (
          <ListItemButton
            key={id}
            onClick={() => setOpen({ id, viaMore: true })}
            sx={{ borderRadius: 1.5, gap: 1.5, py: 1 }}
          >
            <Box component="span" aria-hidden sx={{ display: 'inline-flex', color: summary ? 'primary.main' : 'text.secondary', '& svg': { fontSize: 20 } }}>
              {ICONS[id]}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {FILTER_LABELS[id]}
              </Typography>
              <Typography variant="caption" color={summary ? 'primary.dark' : 'text.secondary'} noWrap component="div">
                {summary || 'Any'}
              </Typography>
            </Box>
            <ChevronRightIcon aria-hidden fontSize="small" sx={{ color: 'text.secondary' }} />
          </ListItemButton>
        );
      })}
    </List>
  );

  const viaMore = Boolean(shownPanel && 'viaMore' in shownPanel && shownPanel.viaMore);
  const anchor = shownPanel ? pills.current[shownPanel.id === 'more' || viaMore ? 'more' : shownPanel.id] ?? null : null;
  const panelId = shownPanel?.id;
  const panelActive = panelId && panelId !== 'more' ? isFilterActive(panelId, state) : extrasSet > 0;

  return (
    <>
      <Box role="group" aria-label="Filters" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        {shown.map((id) => (
          <FilterPill
            key={id}
            ref={(element) => {
              pills.current[id] = element;
            }}
            label={FILTER_LABELS[id]}
            icon={ICONS[id]}
            summary={describeFilter(id, state, names, today)}
            warning={hasBackwardsDates(id, state)}
            open={ownPanel === id}
            onOpen={() => setOpen({ id })}
            onClear={() => onChange(clearFilter(id))}
          />
        ))}
        <FilterPill
          ref={(element) => {
            pills.current.more = element;
          }}
          label="More filters"
          icon={<TuneOutlinedIcon />}
          summary=""
          badge={extrasSet}
          open={Boolean(open && (open.id === 'more' || ('viaMore' in open && open.viaMore)))}
          onOpen={() => setOpen({ id: 'more' })}
        />
        {(activeFilterCount(state) > 0 || searching) && (
          <Button
            size="small"
            color="inherit"
            startIcon={<CloseRoundedIcon />}
            onClick={onClearAll}
            sx={{ color: 'text.secondary', ml: 0.5 }}
          >
            Clear all
          </Button>
        )}
      </Box>

      <FilterSurface
        open={Boolean(open && anchor)}
        anchorEl={anchor}
        onClose={close}
        title={!panelId || panelId === 'more' ? 'More filters' : FILTER_LABELS[panelId]}
        width={panelId ? WIDTHS[panelId] : undefined}
        matchCount={matchCount}
        onClear={
          panelActive
            ? () =>
                onChange(
                  panelId && panelId !== 'more'
                    ? clearFilter(panelId)
                    : { ...clearFilter('owner'), ...clearFilter('source'), ...clearFilter('stage') }
                )
            : undefined
        }
        onBack={viaMore ? () => setOpen({ id: 'more' }) : undefined}
      >
        {panelId === 'more' ? moreMenu : panelId ? editor(panelId) : null}
      </FilterSurface>
    </>
  );
};
